<script setup lang="ts">

import {useModyApi} from "~/middelware/ModyApi";
import {Cart, updateCart} from "~/utils/Cart";
import {Product} from "~/utils/Product";
import {useUserDataStorage} from "~/store/userdata";
import {useToast} from "primevue/usetoast";
import {useReCaptcha} from 'vue-recaptcha-v3';
import CartDataTable from "~/components/shop/CartDataTable.vue";
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import StepPanels from 'primevue/steppanels';
import StepItem from 'primevue/stepitem';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';


useSeoMeta({
  title: "Jetzt anfragen!",
  ogTitle: "Jetzt anfragen!",
  description:"Mit diesem Formular kann ganz einfach eine unverbindliche Anfrage für ein Event gestellt werden.",
  ogDescription:"Mit diesem Formular kann ganz einfach eine unverbindliche Anfrage für ein Event gestellt werden.",
})

const cart = ref<Cart>(new Cart());
const router = useRouter()
const route = useRoute()
const api = useModyApi()
const loaded = ref(false)
const userStore = useUserDataStorage()
const loading = ref(false)
const toast = useToast()
const {requestData} = storeToRefs(userStore)
const activeStep = ref(1)

const recaptchaInstance = useReCaptcha();
const recaptcha = async () => {
  // optional you can await for the reCaptcha load
  await recaptchaInstance?.recaptchaLoaded();
  
  // get the token, a custom action could be added as argument to the method
  return recaptchaInstance?.executeRecaptcha('yourActionHere');
};

function dateFromChanged(){
  if(requestData.value.date_to < requestData.value.date_from){
    requestData.value.date_to = requestData.value.date_from
  }
  requestData.value.eventDays = calcEventDays()
}

onMounted( async ()=>{
  await loadData()
  loaded.value = true
  const productToAdd = Number(route.query.productToAdd)
  if(productToAdd && !Number.isNaN(productToAdd)){
    const tempProduct = await api.getProduct(productToAdd)
    cart.value.products = [{
      count: 1, product: tempProduct,
      factor: 1,
      price: tempProduct.Price,
      sectionName: "",
      comment: "",
      editSectionName: false
    }]
  }
})

async function removeFromCart(index:number){
  cart.value.removeProduct(index)
}
async function loadData(){
  if(userStore.loggedIn){

    cart.value = await api.getUserCart()
  }
  else{
    if(!userStore.browserCart){
      userStore.browserCart = new Cart()
    }
    cart.value = new Cart().fromJSON(userStore.browserCart)
  }
}

function resetData(){
  requestData.value.description = ""
  requestData.value.telephone = ""
  requestData.value.email = ""
  requestData.value.company_name = ""
  requestData.value.name = ""
  requestData.value.address = ""
  requestData.value.city = ""
  requestData.value.eventDays = 1
  requestData.value.date_from = new Date()
  requestData.value.date_to = new Date()
  cart.value.products = []
}
async function submitRequest(){
  let token
  try {
    token = await recaptcha()
  }
  catch (e) {
    console.log(e)
  }
  
  if(!token) {
    toast.add({detail:"Es ist leider etwas schief gelaufen!",severity:"error",life:5000})
    return
  }
  loading.value = true
  const success = await api.sendRequest(requestData,cart.value)
  setTimeout(()=>{
    loading.value = false
    if(success){
      toast.add({detail:"Anfrage wurde gesendet!",severity:"success",life:5000})
      resetData()
      activeStep.value = 1
      useTrackEvent("send_request_success")
    }
    else{
      toast.add({detail:"Es ist leider etwas schief gelaufen!",severity:"error",life:5000})
      useTrackEvent("send_request_failure")
    }
  },2000)
}

function calcEventDays(){
  // Konvertiere beide Datumswerte in Millisekunden
  const msProTag = 1000 * 60 * 60 * 24; // Millisekunden pro Tag
  const start = new Date(requestData.value.date_from).getTime();
  const ende = new Date(requestData.value.date_to).getTime();
  
  // Berechne die Differenz in Millisekunden und teile durch msProTag, runde dann auf
  const differenzInTagen = Math.abs((ende - start) / msProTag);
  
  return Math.ceil(differenzInTagen)+1; // Runden auf den nächsten ganzen Tag
}

function nextPage(activateCallback: (step: number) => void){
  console.log(activeStep.value)
  activateCallback(activeStep.value+1)
}
function previousPage(activateCallback: (step: number) => void){
  activateCallback(activeStep.value-1)
}

</script>
<template>
  <div>
    <Toast/>
    <Dialog v-model:visible="loading" modal :style="{background:'var(--surface-ground)',width:'unset !important', padding:'50px'}">
      <template #container>
        <ProgressSpinner/>
      </template>
    </Dialog>
    <div class="content" v-if="cart">
      <h1>Anfrage</h1>
      <div class="requestInfo">Mit diesem Formular kannst du eine unverbindliche Anfrage stellen – für Firmenveranstaltungen genauso wie für Hochzeiten, Konzerte und regionale Events.
        Wir melden uns danach umgehend mit einem maßgeschneiderten Angebot, das zu deinem Ablauf, deinem Budget und der gewünschten technischen Betreuung passt.</div>
        <div v-if="!loaded" class="mt-6">
          <Skeleton height="50px" style="margin-bottom:10px"></Skeleton>
          <Skeleton height="20px" style="margin-bottom:30px"></Skeleton>
          <Skeleton height="50px" style="margin-bottom:10px"></Skeleton>
          <Skeleton height="20px" style="margin-bottom:30px"></Skeleton>
          <Skeleton height="50px" style="margin-bottom:10px"></Skeleton>
          <Skeleton height="20px" style="margin-bottom:30px"></Skeleton>
          <Skeleton height="50px" style="margin-bottom:10px"></Skeleton>
          <Skeleton height="20px" style="margin-bottom:30px"></Skeleton>
        </div>
        <Stepper v-model:value="activeStep" linear v-else>
          <StepPanels style="padding:0">
            <StepItem :value="1" v-if="cart.products.length != 0">
              <Step>Equipment</Step>
              <StepPanel v-slot="{activateCallback}">
                  <div v-if="cart.products.length == 0">
                    <div class="requestInfo">
                      Dein Warenkorb ist leer. Du kannst entweder noch Equipment aus unserem Sortiment hinzufügen und wir erstellen dir
                      ein Angebot dafür, oder du schickst uns einfach so eine Anfrage in dem du zum nächsten Schritt gehst.
                    </div>
                    <br>
                    <div @click="router.push('/shop')" style="text-decoration: underline; cursor:pointer">zum Sortiment -></div>
                  </div>
                  <CartDataTable v-else-if="cart.products.length != 0"  :cartProducts="cart.products" @removeProduct="removeFromCart" @updateCart="updateCart(cart)" :loaded="loaded"></CartDataTable>
                  
                  <div class="d-flex pt-4 justify-content-end">
                    <Button type="submit" label="Zur Anfrage" icon="pi pi-arrow-right" iconPos="right" @click="nextPage(activateCallback)" />
                  </div>
              </StepPanel>
            </StepItem>
            <StepItem :value="cart.products.length != 0 ? 2 : 1">
              <Step>Zeitraum</Step>
              <StepPanel v-slot="{activateCallback}">
                  <div class="row">
                    <div class="col input-container">
                      <label for="from_date" style="display:block;">Von*</label>
                      <Calendar class="w-100" id="from_date" name="from_date" dateFormat="dd.mm.yy" v-model="requestData.date_from" :min-date="new Date()"
                      @date-select="dateFromChanged()"/>
                    </div>
                    <div class="col input-container">
                      <label for="to_date" style="display:block;">Bis*</label>
                      <Calendar class="w-100" id="to_date" name="to_date" dateFormat="dd.mm.yy" v-model="requestData.date_to" :min-date="requestData.date_from"
                      @date-select="dateFromChanged()"/>
                    </div>
                  </div>
                  <div class="row input-container" v-if="calcEventDays() > 1">
                    <InputNumber :allow-empty="false" button-layout="stacked" show-buttons id="eventDays" class="w-50" :min="1" :max="calcEventDays()" v-model="requestData.eventDays"></InputNumber>
                  </div>
                  <div class="row">
                    <div class="col d-flex">
                      <div class=" align-middle" v-if="requestData.eventDays>1">
                        {{ requestData.eventDays }} Veranstaltungstage 
                        (Faktor {{ getDayFactor(requestData.eventDays).toLocaleString("de-AT") }})
                      </div>
                    </div>
                  </div>
                  <div v-if="cart.products.length == 0" class="d-flex justify-content-end mt-4">
                    <Button type="submit" label="Weiter" icon="pi pi-arrow-right" iconPos="right" @click="nextPage(activateCallback)" />
                  </div>
                  <div v-else class="d-flex justify-content-between mt-4">
                    <Button label="Zurück" severity="secondary" icon="pi pi-arrow-left" @click="previousPage(activateCallback)" />
                    <Button type="submit" label="Weiter" icon="pi pi-arrow-right" iconPos="right" @click="nextPage(activateCallback)" />
                  </div>

              </StepPanel>
            </StepItem>
            <StepItem :value="cart.products.length != 0 ? 3 : 2">
              <Step >Details</Step>
              <StepPanel v-slot="{activateCallback}">
                  <form @submit.prevent="nextPage(activateCallback)">
                    <div class="row">
                      <div class="col input-container">
                        <label>Name*</label>
                        <InputText class="w-100" type="name" name="name" autocomplete="name" v-model="requestData.name" required
                        placeholder="Dein Vor- und Nachname"/>
                      </div>
                      <div class="input-container col">
                        <label>Email*</label>
                        <InputText class="w-100" type="email" name="email" autocomplete="email" v-model="requestData.email" required
                        placeholder=""/>
                      </div>
                    </div>
                    <div class="row opacity-70">
                      <div class="col input-container">
                        <label>Firma</label>
                        <InputText class="w-100" type="name" name="name" autocomplete="organization" v-model="requestData.company_name"
                        placeholder="Firmenname (optional)"/>
                      </div>
                      <div class="col input-container">
                        <label>Adresse</label>
                        <InputText class="w-100" type="name" name="name" autocomplete="street-address" v-model="requestData.address"
                        placeholder="Straße + Hausnummer"/>
                      </div>
                    </div>
                    <div class="row opacity-70">
                      <div class="col input-container">
                        <label>PLZ und Ort</label>
                        <InputText class="w-100" type="name" name="name" autocomplete="city" v-model="requestData.city"
                        placeholder="PLZ + Ort"/>
                      </div>
                      <div class="input-container col">
                        <label>Telefonnummer</label>
                        <InputText class="w-100" type="tel" name="tel" autocomplete="tel" v-model="requestData.telephone"
                        placeholder=""/>
                      </div>
                    </div>
                    <div class="input-container">
                      <label for="description">Beschreibung*</label>
                      <Textarea id="description" name="description" v-model="requestData.description" rows="5" autoResize style="width:100%" required
                      placeholder="Ich plane..."/>
                    </div>
                    <div class="d-flex pt-4 justify-content-between">
                      <Button label="Zurück" severity="secondary" icon="pi pi-arrow-left" @click="previousPage(activateCallback)" />
                      <Button type="submit" label="Weiter" icon="pi pi-arrow-right" iconPos="right"/>
                    </div>
                  </form>
              </StepPanel>
            </StepItem>

            <StepItem :value="cart.products.length != 0 ? 4 : 3">
              <Step>Zusammenfassung</Step>
              <StepPanel v-slot="{activateCallback}">
                  <div>
                    <Divider>Zeitraum</Divider>
                    <div>Datum: {{requestData.date_from.toLocaleDateString("de-at")}} bis {{requestData.date_to.toLocaleDateString("de-at")}}</div>
                    <div v-if="calcEventDays() > 1">Veranstaltungstage: {{ requestData.eventDays }} (Faktor {{ getDayFactor(requestData.eventDays) }}x)</div>
                    <Divider>Adresse</Divider>
                    <div>{{ requestData.name }}</div>
                    <div>{{ requestData.company_name }}</div>
                    <div>{{ requestData.address }}</div>
                    <div>{{ requestData.city }}</div>
                    <Divider>Beschreibung</Divider>
                    <div>{{ requestData.description }}</div>
                  </div>
                  <div class="d-flex pt-4 justify-content-between mt-5">
                    <Button label="Zurück" severity="secondary" icon="pi pi-arrow-left" @click="previousPage(activateCallback)" />
                    <Button label="Abschicken" icon="pi pi-check" iconPos="right" @click="submitRequest" />
                  </div>
              </StepPanel>
            </StepItem>
          </StepPanels>
        </Stepper>
      </div>
    </div>
  </template>
  <style lang="scss" scoped>
  
  :deep(.p-progress-spinner-circle){
    stroke:$myOrange !important;
  }
  .requestInfo{
    line-height:25px;
  }
  :deep(.p-steppanel){
    background: transparent ;
  }
  input, label{
    display:block;
  }
  .input-container{
    margin-top:20px;
    label{
      margin-bottom:5px;
    }
  }
  :deep(.p-error){
    display: block;
    max-width: 190px;
  }
  .content{
    text-align:left;
    margin-bottom:100px;
    padding-top: clamp(2.5rem, 5vw, 4rem);

    h1{
      margin: 0 0 0.8rem;
      color: white;
      font-size: clamp(2.4rem, 5vw, 3.35rem);
      font-weight: 750;
      line-height: 1.05;
      letter-spacing: -0.035em;
    }
  }
  .requestInfo{
    max-width: 760px;
    color: rgb(214 211 209);
    font-size: 1.04rem;
    line-height: 1.65;
  }
  :deep(.p-divider-content){
    background: transparent;
  }
  :deep(.p-step-header){
    padding-left: 0px;
  }
  :deep(.p-stepper-separator){
    margin-left: 0px;
    display: none;
  }
  :deep(.p-steppanel-content){
    margin-left:0px;
  }
  :deep(.p-stepitem:last-of-type .p-steppanel){
    padding-inline-start: unset;
  }
</style>
